/**
 * Bangladesh VAT rates and sector presets.
 * Source: NBR — VAT and Supplementary Duty Act 2012 (with subsequent SROs).
 * Standard 15% plus several truncated/reduced rates by sector.
 *
 * Last updated: 2026-05-10.
 */

export type Bilingual = { en: string; bn: string };

export interface VatRateOption {
  id: string;
  ratePct: number;
  label: Bilingual;
  description: Bilingual;
}

export const BD_VAT_RATES: VatRateOption[] = [
  {
    id: "standard",
    ratePct: 15,
    label: { en: "15% — Standard", bn: "১৫% — সাধারণ" },
    description: {
      en: "Default VAT rate for most goods and services in Bangladesh.",
      bn: "বাংলাদেশের অধিকাংশ পণ্য ও সেবার জন্য প্রযোজ্য সাধারণ ভ্যাট হার।",
    },
  },
  {
    id: "trunc-10",
    ratePct: 10,
    label: { en: "10% — Truncated", bn: "১০% — ট্রাঙ্কেটেড" },
    description: {
      en: "Reduced rate applied to selected sectors via SRO.",
      bn: "এসআরও অনুযায়ী নির্দিষ্ট খাতে প্রযোজ্য হ্রাসকৃত হার।",
    },
  },
  {
    id: "trunc-7-5",
    ratePct: 7.5,
    label: { en: "7.5% — Truncated", bn: "৭.৫% — ট্রাঙ্কেটেড" },
    description: {
      en: "Common for furniture, non-AC restaurants and similar services.",
      bn: "আসবাবপত্র, নন-এসি রেস্তোরাঁ ইত্যাদিতে প্রযোজ্য।",
    },
  },
  {
    id: "trunc-5",
    ratePct: 5,
    label: { en: "5% — Truncated", bn: "৫% — ট্রাঙ্কেটেড" },
    description: {
      en: "Selected items: MS Rod, IT-enabled services, certain construction.",
      bn: "এমএস রড, আইটি সেবা, কিছু নির্মাণ সামগ্রীতে প্রযোজ্য।",
    },
  },
  {
    id: "trunc-2-4",
    ratePct: 2.4,
    label: { en: "2.4% — Truncated", bn: "২.৪% — ট্রাঙ্কেটেড" },
    description: {
      en: "Medicine retailer and a few other narrow categories.",
      bn: "ঔষধ খুচরা বিক্রেতা ও কিছু সংকীর্ণ খাতে প্রযোজ্য।",
    },
  },
  {
    id: "exempt",
    ratePct: 0,
    label: { en: "0% — Exempt / zero-rated", bn: "০% — অব্যাহতিপ্রাপ্ত" },
    description: {
      en: "Exports and exempted essentials. No VAT charged.",
      bn: "রপ্তানি ও অব্যাহতিপ্রাপ্ত পণ্য — কোনো ভ্যাট নেই।",
    },
  },
];

export interface SectorPreset {
  id: string;
  ratePct: number;
  name: Bilingual;
  note: Bilingual;
}

export const BD_VAT_SECTORS: SectorPreset[] = [
  {
    id: "general",
    ratePct: 15,
    name: { en: "General goods & services", bn: "সাধারণ পণ্য ও সেবা" },
    note: { en: "Default 15% standard rate.", bn: "ডিফল্ট ১৫% সাধারণ হার।" },
  },
  {
    id: "restaurant-ac",
    ratePct: 15,
    name: { en: "Restaurant (AC)", bn: "রেস্তোরাঁ (এসি)" },
    note: { en: "Air-conditioned restaurants — 15%.", bn: "শীতাতপ নিয়ন্ত্রিত রেস্তোরাঁ — ১৫%।" },
  },
  {
    id: "restaurant-nonac",
    ratePct: 7.5,
    name: { en: "Restaurant (Non-AC)", bn: "রেস্তোরাঁ (নন-এসি)" },
    note: { en: "Non-AC restaurants — truncated 7.5%.", bn: "নন-এসি রেস্তোরাঁ — ট্রাঙ্কেটেড ৭.৫%।" },
  },
  {
    id: "furniture-mfg",
    ratePct: 7.5,
    name: { en: "Furniture manufacturer", bn: "আসবাবপত্র প্রস্তুতকারক" },
    note: { en: "Truncated 7.5% at manufacturing stage.", bn: "প্রস্তুতকরণ পর্যায়ে ট্রাঙ্কেটেড ৭.৫%।" },
  },
  {
    id: "furniture-seller",
    ratePct: 7.5,
    name: { en: "Furniture seller (showroom)", bn: "আসবাবপত্র বিক্রেতা (শোরুম)" },
    note: { en: "Showroom sale — 7.5%.", bn: "শোরুম বিক্রি — ৭.৫%।" },
  },
  {
    id: "ms-rod",
    ratePct: 5,
    name: { en: "MS Rod", bn: "এমএস রড" },
    note: { en: "Iron rod manufacturing — 5%.", bn: "লোহা/রড উৎপাদন — ৫%।" },
  },
  {
    id: "medicine-retail",
    ratePct: 2.4,
    name: { en: "Medicine retailer", bn: "ঔষধ খুচরা বিক্রেতা" },
    note: { en: "Pharmacy retail — 2.4%.", bn: "ফার্মেসি খুচরা বিক্রি — ২.৪%।" },
  },
  {
    id: "it-services",
    ratePct: 5,
    name: { en: "IT services", bn: "আইটি সেবা" },
    note: { en: "IT-enabled services — 5%.", bn: "আইটি সেবা — ৫%।" },
  },
  {
    id: "hotel-3star",
    ratePct: 15,
    name: { en: "Hotel (3-star and above)", bn: "হোটেল (৩-তারকা+)" },
    note: { en: "3-star and above accommodation — 15%.", bn: "৩-তারকা ও তদূর্ধ্ব হোটেল — ১৫%।" },
  },
];

export const VAT_LAST_UPDATED = "2026-05-10";
export const NBR_VAT_URL = "https://nbr.gov.bd/";
